import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import {
    MIN_DAILY_STREAMING_LIMIT_SECONDS,
    MIN_MAX_SESSION_TIME_SECONDS,
    type VideoStreamSettingsValues,
} from '@/domain/videoStreamSettings';

import styles from './EditVideoStreamSettingsModal.module.scss';

const TWELVE_HOURS_SECONDS = 12 * 3600;

function parseSecondsInput(value: string): { ok: true; n: number } | { ok: false } {
    if (value.trim() === '' || value === '-' || value === '–') {
        return { ok: false };
    }
    const n = Math.trunc(Number(value));
    if (!Number.isFinite(n)) {
        return { ok: false };
    }
    return { ok: true, n };
}

interface EditVideoStreamSettingsModalProps {
    isOpen: boolean;
    title: string;
    initialValues: VideoStreamSettingsValues;
    isSaving: boolean;
    onClose: () => void;
    onSubmit: (values: VideoStreamSettingsValues) => Promise<void>;
    /** True when "edit entire location" and bioscopes on that location do not all share the same values. */
    locationSettingsDiffer?: boolean;
}

export default function EditVideoStreamSettingsModal(props: EditVideoStreamSettingsModalProps) {
    const { isOpen, title, initialValues, isSaving, onClose, onSubmit, locationSettingsDiffer } =
        props;
    const { t } = useTranslation();
    const dailyMaxMessage = t('internal.videoStreamSettings.errors.dailyMax', {
        seconds: TWELVE_HOURS_SECONDS,
    });
    const [draft, setDraft] = useState<VideoStreamSettingsValues>(initialValues);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        setDraft(initialValues);
        setValidationError(null);
    }, [initialValues]);

    const validate = (d: VideoStreamSettingsValues): string | null => {
        if (!Number.isInteger(d.maxContinuousDurationSeconds)) {
            return t('internal.videoStreamSettings.errors.sessionWhole');
        }
        if (!Number.isInteger(d.dailyLimitSeconds)) {
            return t('internal.videoStreamSettings.errors.dailyWhole');
        }
        if (d.maxContinuousDurationSeconds < MIN_MAX_SESSION_TIME_SECONDS) {
            return t('internal.videoStreamSettings.errors.sessionMin', {
                seconds: MIN_MAX_SESSION_TIME_SECONDS,
            });
        }
        if (d.dailyLimitSeconds < MIN_DAILY_STREAMING_LIMIT_SECONDS) {
            return t('internal.videoStreamSettings.errors.dailyMin', {
                seconds: MIN_DAILY_STREAMING_LIMIT_SECONDS,
            });
        }
        if (d.maxContinuousDurationSeconds > TWELVE_HOURS_SECONDS) {
            return t('internal.videoStreamSettings.errors.sessionMax', {
                seconds: TWELVE_HOURS_SECONDS,
            });
        }
        if (d.dailyLimitSeconds > TWELVE_HOURS_SECONDS) {
            return dailyMaxMessage;
        }
        return null;
    };

    return (
        <ModalComponent isOpen={isOpen} onClose={onClose} centered={false} title={title}>
            <ModalBody>
                {locationSettingsDiffer && (
                    <Alert tone="info" size="small" live="off" className={styles.notice}>
                        {t('internal.videoStreamSettings.modal.settingsDiffer')}
                    </Alert>
                )}
                <FormField
                    label={t('internal.videoStreamSettings.modal.maxSessionLabel')}
                    hint={t('internal.videoStreamSettings.modal.maxSessionHint', {
                        seconds: MIN_MAX_SESSION_TIME_SECONDS,
                    })}>
                    <TextField
                        type="number"
                        inputMode="numeric"
                        value={String(draft.maxContinuousDurationSeconds)}
                        onChange={(event) => {
                            setValidationError(null);
                            const parsed = parseSecondsInput(event.target.value);
                            if (!parsed.ok) {
                                return;
                            }
                            setDraft((previous) => ({
                                ...previous,
                                maxContinuousDurationSeconds: parsed.n,
                            }));
                        }}
                    />
                </FormField>
                <FormField
                    label={t('internal.videoStreamSettings.modal.dailyLimitLabel')}
                    hint={t('internal.videoStreamSettings.modal.dailyLimitHint', {
                        min: MIN_DAILY_STREAMING_LIMIT_SECONDS,
                        max: TWELVE_HOURS_SECONDS,
                    })}>
                    <TextField
                        type="number"
                        inputMode="numeric"
                        value={String(draft.dailyLimitSeconds)}
                        onChange={(event) => {
                            setValidationError(null);
                            const parsed = parseSecondsInput(event.target.value);
                            if (!parsed.ok) {
                                return;
                            }
                            if (parsed.n > TWELVE_HOURS_SECONDS) {
                                setValidationError(dailyMaxMessage);
                                return;
                            }
                            setDraft((previous) => ({
                                ...previous,
                                dailyLimitSeconds: parsed.n,
                            }));
                        }}
                    />
                </FormField>
                <FormField label={t('internal.videoStreamSettings.modal.defaultQuality')}>
                    <Select
                        value={draft.defaultQuality}
                        onChange={(event) =>
                            setDraft((previous) => ({
                                ...previous,
                                defaultQuality:
                                    event.target.value === 'STANDARD' ? 'STANDARD' : 'HD',
                            }))
                        }>
                        <option value="HD">HD</option>
                        <option value="STANDARD">
                            {t('internal.videoStreamSettings.qualityStandard')}
                        </option>
                    </Select>
                </FormField>
            </ModalBody>
            {validationError && (
                <Alert tone="danger" size="small">
                    {validationError}
                </Alert>
            )}
            <ModalFooter>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.actions.cancel')}
                </Button>
                <Button
                    variant="primary"
                    disabled={isSaving}
                    onClick={() => {
                        const err = validate(draft);
                        if (err) {
                            setValidationError(err);
                            return;
                        }
                        void onSubmit(draft);
                    }}>
                    {t('common.actions.save')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}
